import React, { useMemo, useId, useState } from 'react'

export function AnimatedChatAvatarS({
  // Layout
  size = 96,
  scale = 1,
  className = "",

  // Behavior
  thinking = false,
  animationMode = "still", // "still" | "rotating"
  durationSeconds = 2, // Speed of the "S" shape rotation
  gradientDurationSeconds = 1, // Speed of the inner gradient rotation (separated)

  // Visuals
  backdropColor = "#353535c1", // Color behind the gradients (fixes transparency if needed)

  // Back-compat: if passed explicitly, it will override gradientDurationSeconds.
  speedMs,

  // Gradient start pose
  startRotationTop = -90,
  startRotationBottom = 90,

  // ClipPath alignment (viewBox units & degrees)
  topPathX = 0,
  topPathY = -18,
  topPathRotation = -80,
  bottomPathX = 0,
  bottomPathY = 8.5,
  bottomPathRotation = -30,

  // Arc length tuning (degrees)
  topOuterExtend = 30,
  topInnerExtend = 22,
  bottomOuterExtend = 30,
  bottomInnerExtend = 22,
}) {
  const uid = useId();

  // Derive durations
  const gradientDurationMs =
    typeof speedMs === "number" && Number.isFinite(speedMs)
      ? Math.max(0, speedMs)
      : Math.max(0, gradientDurationSeconds * 1000);

  const arcRotating = thinking && animationMode === "rotating";

  // Donut segment generator (evenodd fill)
  const donutSegmentPath = useMemo(() => {
    const polar = (cx, cy, r, aDeg) => {
      const a = (Math.PI / 180) * aDeg;
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    };

    const segment = ({
      cx,
      cy,
      rOuter,
      rInner,
      a0Outer,
      a1Outer,
      a0Inner,
      a1Inner,
    }) => {
      const [x0o, y0o] = polar(cx, cy, rOuter, a0Outer);
      const [x1o, y1o] = polar(cx, cy, rOuter, a1Outer);
      const [x0i, y0i] = polar(cx, cy, rInner, a0Inner);
      const [x1i, y1i] = polar(cx, cy, rInner, a1Inner);

      const delta = ((a1Outer - a0Outer) % 360 + 360) % 360;
      const largeArc = delta > 180 ? 1 : 0;

      return [
        `M ${x0o.toFixed(3)} ${y0o.toFixed(3)}`,
        `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x1o.toFixed(3)} ${y1o.toFixed(3)}`,
        `L ${x1i.toFixed(3)} ${y1i.toFixed(3)}`,
        `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x0i.toFixed(3)} ${y0i.toFixed(3)}`,
        `Z`,
      ].join(" ");
    };

    const topBase = { a0: 205, a1: 25 };
    const bottomBase = { a0: 335, a1: 155 };

    const top = segment({
      cx: 50,
      cy: 40,
      rOuter: 30,
      rInner: 17,
      a0Outer: topBase.a0 - topOuterExtend,
      a1Outer: topBase.a1 + topOuterExtend,
      a0Inner: topBase.a0 - topInnerExtend,
      a1Inner: topBase.a1 + topInnerExtend,
    });

    const bottom = segment({
      cx: 50,
      cy: 60,
      rOuter: 30,
      rInner: 17,
      a0Outer: bottomBase.a0 - bottomOuterExtend,
      a1Outer: bottomBase.a1 + bottomOuterExtend,
      a0Inner: bottomBase.a0 - bottomInnerExtend,
      a1Inner: bottomBase.a1 + bottomInnerExtend,
    });

    return { top, bottom };
  }, [topOuterExtend, topInnerExtend, bottomOuterExtend, bottomInnerExtend]);

  const clipTopId = `${uid}-clip-top`;
  const clipBottomId = `${uid}-clip-bottom`;

  const topCenter = { x: 50, y: 40 };
  const bottomCenter = { x: 50, y: 60 };

  // Rotation centers for gradients (percentages within 0..100 viewBox)
  // Adjusted for new viewBox="-50 -50 200 200" and foreignObject x="-50" y="-50"
  // We dynamically calculate the origin to match the translated center of the arc
  // Offset = Center + Translation - ForeignObjectOrigin(-50)
  const topOrigin = `${topCenter.x + 50 + topPathX}px ${topCenter.y + 50 + topPathY}px`;
  const bottomOrigin = `${bottomCenter.x + 50 + bottomPathX}px ${bottomCenter.y + 50 + bottomPathY}px`;

  return (
    <div
      className={`relative inline-block select-none ${className}`}
      style={{
        width: size * scale * 1.5,
        height: size * scale * 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      }}
      aria-label={thinking ? "Avatar (thinking)" : "Avatar"}
      role="img"
    >
      <svg
        width={size * scale * 1.5}
        height={size * scale * 1.5}
        viewBox="-50 -50 200 200"
        style={{ overflow: "visible", display: "block" }}
        aria-hidden="true"
      >
        <defs>
          <clipPath id={clipTopId} clipPathUnits="userSpaceOnUse">
            <path
              d={donutSegmentPath.top}
              fillRule="evenodd"
              transform={`translate(${topPathX} ${topPathY}) rotate(${topPathRotation} ${topCenter.x} ${topCenter.y})`}
            >
              {arcRotating ? (
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  additive="sum"
                  dur={`${durationSeconds}s`}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.85 0 0.15 1"
                  values={`0 ${topCenter.x} ${topCenter.y}; 360 ${topCenter.x} ${topCenter.y}`}
                />
              ) : null}
            </path>
          </clipPath>

          <clipPath id={clipBottomId} clipPathUnits="userSpaceOnUse">
            <path
              d={donutSegmentPath.bottom}
              fillRule="evenodd"
              transform={`translate(${bottomPathX} ${bottomPathY}) rotate(${bottomPathRotation} ${bottomCenter.x} ${bottomCenter.y})`}
            >
              {arcRotating ? (
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  additive="sum"
                  dur={`${durationSeconds}s`}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.85 0 0.15 1"
                  values={`0 ${bottomCenter.x} ${bottomCenter.y}; 360 ${bottomCenter.x} ${bottomCenter.y}`}
                />
              ) : null}
            </path>
          </clipPath>
        </defs>

        {/* TOP */}
        <g clipPath={`url(#${clipTopId})`}>
          <foreignObject x="-50" y="-50" width="200" height="200">
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className="foWrap"
              style={{
                width: "100%",
                height: "100%",
                transformOrigin: topOrigin,
                ["--r0"]: `${startRotationTop}deg`,
                animation: thinking
                  ? `spin360From ${gradientDurationMs}ms cubic-bezier(0.85, 0, 0.15, 1) infinite`
                  : "none",
                transform: `rotate(${startRotationTop}deg)`,
              }}
            >
              <div className="foGradient" style={{ backgroundColor: backdropColor }} />
            </div>
          </foreignObject>
        </g>

        {/* BOTTOM */}
        <g clipPath={`url(#${clipBottomId})`}>
          <foreignObject x="-50" y="-50" width="200" height="200">
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className="foWrap"
              style={{
                width: "100%",
                height: "100%",
                transformOrigin: bottomOrigin,
                ["--r0"]: `${startRotationBottom}deg`,
                animation: thinking
                  ? `spin360From ${gradientDurationMs}ms cubic-bezier(0.85, 0, 0.15, 1) infinite reverse`
                  : "none",
                transform: `rotate(${startRotationBottom}deg)`,
              }}
            >
              <div className="foGradient" style={{ backgroundColor: backdropColor }} />
            </div>
          </foreignObject>
        </g>
      </svg>

      <style>{`
        @keyframes spin360From {
          0% { transform: rotate(var(--r0)); }
          100% { transform: rotate(calc(var(--r0) + 360deg)); }
        }

        .foWrap {
          position: relative;
          overflow: visible;
        }

        /* Official palette:
           black #000000, blue #3d20ee, purple #8e12b2, white #ffffff */
        .foGradient {
          position: absolute;
          inset: -12%;
          background-image:
            radial-gradient(circle at 0% 0%, #8e12b2, rgba(142, 18, 178, 0) 60%),
            radial-gradient(circle at 100% 0%, #3d20ee, rgba(61, 32, 238, 0) 60%),
            radial-gradient(circle at 100% 100%, #ffffff, rgba(255, 255, 255, 0) 62%),
            radial-gradient(circle at 0% 100%, #000000, rgba(0, 0, 0, 0) 62%);
          filter: saturate(1.15) contrast(1.05);
        }
      `}</style>
    </div>
  );
}

/**
 * Demo (default export) for quick testing.
 */
export default function AnimatedChatAvatarSDemo() {
  const [thinking, setThinking] = useState(true);
  const [animationMode, setAnimationMode] = useState("still"); // "still" | "rotating"
  const [durationSec, setDurationSec] = useState(1.6);

  const [scale, setScale] = useState(1);
  const [bg, setBg] = useState("black");
  const [custom, setCustom] = useState("#ffffff");

  // ClipPath tuning (tool)
  const [topX, setTopX] = useState(0);
  const [topY, setTopY] = useState(-18);
  const [topR, setTopR] = useState(-80);
  const [botX, setBotX] = useState(0);
  const [botY, setBotY] = useState(8.5);
  const [botR, setBotR] = useState(-30);

  // Gradient initial pose
  const [gTopR, setGTopR] = useState(-90);
  const [gBotR, setGBotR] = useState(90);

  // Arc length tool (outer/inner)
  const [topOuterExt, setTopOuterExt] = useState(30);
  const [topInnerExt, setTopInnerExt] = useState(22);
  const [botOuterExt, setBotOuterExt] = useState(30);
  const [botInnerExt, setBotInnerExt] = useState(22);

  const bgStyle =
    bg === "black"
      ? { background: "#000" }
      : bg === "white"
      ? { background: "#fff" }
      : bg === "gradient"
      ? {
          background:
            "radial-gradient(circle at 30% 20%, rgba(255, 90, 200, 0.35), rgba(0,0,0,0) 55%), radial-gradient(circle at 70% 80%, rgba(90, 160, 255, 0.35), rgba(0,0,0,0) 55%), linear-gradient(135deg, #0b0b12, #141427, #0b0b12)",
        }
      : { background: custom };

  const textColor = bg === "white" || bg === "custom" ? "#111" : "#fff";
  const subTextColor =
    bg === "white" || bg === "custom" ? "rgba(17,17,17,0.70)" : "rgba(255,255,255,0.72)";

  const sliderRow = (label, value, setValue, min, max, step = 1) => (
    <div className="flex items-center gap-2">
      <div className="w-[120px] text-xs" style={{ color: subTextColor }}>
        {label}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(parseFloat(e.target.value))}
        className="w-[220px]"
      />
      <div className="w-[56px] text-right font-mono text-xs" style={{ color: subTextColor }}>
        {typeof value === "number" ? value.toFixed(step < 1 ? 2 : 0) : String(value)}
      </div>
    </div>
  );

  return (
    <div className="min-h-[360px] w-full p-6" style={bgStyle}>
      <div className="mx-auto flex max-w-[920px] flex-col gap-4">
        {/* Top controls */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setThinking((v) => !v)}
              className="rounded-xl px-4 py-2 text-sm font-semibold shadow"
              style={{
                background: "rgba(255,255,255,0.10)",
                color: textColor,
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
              }}
            >
              {thinking ? "Stop thinking" : "Start thinking"}
            </button>

            <div
              className="rounded-xl px-3 py-2 text-sm shadow"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
                color: textColor,
              }}
            >
              <label className="mr-2">Background</label>
              <select
                value={bg}
                onChange={(e) => setBg(e.target.value)}
                className="rounded-lg px-2 py-1"
              >
                <option value="black">Black</option>
                <option value="white">White</option>
                <option value="gradient">Gradient</option>
                <option value="custom">Custom</option>
              </select>
              {bg === "custom" ? (
                <input
                  aria-label="Custom background color"
                  type="color"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="ml-2 h-8 w-10 cursor-pointer rounded"
                />
              ) : null}
            </div>
          </div>

          <div className="text-xs" style={{ color: subTextColor }}>
            scale: <span className="font-mono">{scale.toFixed(2)}</span> • mode:{" "}
            <span className="font-mono">{animationMode}</span> • duration:{" "}
            <span className="font-mono">{durationSec.toFixed(1)}s</span>
          </div>
        </div>

        {/* Main row */}
        <div className="grid gap-4 md:grid-cols-[360px_1fr]">
          {/* Chat bubble */}
          <div
            className="rounded-2xl p-5 shadow"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div className="flex items-center gap-3">
              <AnimatedChatAvatarS
                size={92}
                scale={scale}
                thinking={thinking}
                animationMode={animationMode}
                durationSeconds={durationSec}
                startRotationTop={gTopR}
                startRotationBottom={gBotR}
                topPathX={topX}
                topPathY={topY}
                topPathRotation={topR}
                bottomPathX={botX}
                bottomPathY={botY}
                bottomPathRotation={botR}
                topOuterExtend={topOuterExt}
                topInnerExtend={topInnerExt}
                bottomOuterExtend={botOuterExt}
                bottomInnerExtend={botInnerExt}
              />

              <div className="flex flex-col">
                <div className="text-sm font-semibold" style={{ color: textColor }}>
                  Assistant
                </div>
                <div className="text-sm" style={{ color: subTextColor }}>
                  {thinking ? "Thinking…" : "Ready."}
                </div>
              </div>
            </div>
          </div>

          {/* Tool panel */}
          <div
            className="rounded-2xl p-4 shadow"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div className="mb-2 text-xs font-semibold" style={{ color: subTextColor }}>
              Animation
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <select
                value={animationMode}
                onChange={(e) => setAnimationMode(e.target.value)}
                className="rounded-lg px-2 py-1"
              >
                <option value="still">still (arcs fixed)</option>
                <option value="rotating">rotating (arcs + gradients)</option>
              </select>
              {sliderRow("Duration (s)", durationSec, setDurationSec, 0.4, 6, 0.1)}
            </div>

            <div className="mb-2 text-xs font-semibold" style={{ color: subTextColor }}>
              ClipPath tool (make it look like an S)
            </div>
            <div className="mb-3">{sliderRow("Scale", scale, setScale, 0.5, 3, 0.05)}</div>

            <div className="grid gap-2">
              {sliderRow("Top X", topX, setTopX, -20, 20, 0.5)}
              {sliderRow("Top Y", topY, setTopY, -20, 20, 0.5)}
              {sliderRow("Top Rot", topR, setTopR, -180, 180, 1)}
              <div className="my-2 h-px" style={{ background: "rgba(255,255,255,0.10)" }} />
              {sliderRow("Bottom X", botX, setBotX, -20, 20, 0.5)}
              {sliderRow("Bottom Y", botY, setBotY, -20, 20, 0.5)}
              {sliderRow("Bottom Rot", botR, setBotR, -180, 180, 1)}
            </div>

            <div className="mt-4 mb-2 text-xs font-semibold" style={{ color: subTextColor }}>
              Arc length (outer / inner)
            </div>
            <div className="grid gap-2">
              {sliderRow("Top OUT", topOuterExt, setTopOuterExt, -60, 60, 1)}
              {sliderRow("Top IN", topInnerExt, setTopInnerExt, -60, 60, 1)}
              <div className="my-2 h-px" style={{ background: "rgba(255,255,255,0.10)" }} />
              {sliderRow("Bot OUT", botOuterExt, setBotOuterExt, -60, 60, 1)}
              {sliderRow("Bot IN", botInnerExt, setBotInnerExt, -60, 60, 1)}
            </div>

            <div className="mt-4 mb-2 text-xs font-semibold" style={{ color: subTextColor }}>
              Gradient start pose
            </div>
            <div className="grid gap-2">
              {sliderRow("Grad Top", gTopR, setGTopR, -180, 180, 1)}
              {sliderRow("Grad Bottom", gBotR, setGBotR, -180, 180, 1)}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        select { background: rgba(255,255,255,0.9); }
      `}</style>
    </div>
  );
}